// iter679 — Twin Cascade Toast.
// When a domain event fires (meal-logged, sleep-logged, lab-uploaded,
// stress-rated, etc.) we render a brief, animated "physiological cascade"
// at the top of the twin grid showing how that one input ripples through
// the body's connected systems.
//
// This is the visualisation of cross_twin_insights for the *predictive*
// direction — it converts an abstract logged event into a felt, dynamic
// chain that mirrors the real physiology.

import React, { useEffect, useState } from "react";

// Cascade catalog — each entry is a 4-step chain triggered by a domain event.
// Step format: { emoji, label, delta } where delta is the projected nudge.
// All copy is short, Bulgarian, and uses pre-existing twin emojis.
const CASCADES = {
  "meal-logged": (payload = {}) => {
    const nis = Number(payload.nis_pct ?? payload.nis ?? 0);
    const heavy = nis >= 25;
    if (heavy) {
      return {
        title: "Хранене с висок инсулинов натиск",
        steps: [
          { emoji: "🍽️", label: "Логнато", delta: `NIS ${Math.round(nis)}%` },
          { emoji: "💉", label: "Метаболитен", delta: "натиск ↑" },
          { emoji: "🔥", label: "hsCRP проекция", delta: "+0.3" },
          { emoji: "🎗️", label: "BHI IGF-1 ос", delta: "напрежение ↑" },
        ],
      };
    }
    return {
      title: "Балансирано хранене",
      steps: [
        { emoji: "🍽️", label: "Логнато", delta: `NIS ${Math.round(nis)}%` },
        { emoji: "💉", label: "Метаболитен", delta: "стабилен" },
        { emoji: "🦠", label: "Микробиом", delta: "+ фибри" },
        { emoji: "🧠", label: "Когнитивен", delta: "стабилно" },
      ],
    };
  },
  "sleep-logged": (payload = {}) => {
    const hours = Number(payload.hours ?? 7);
    if (hours < 6) {
      return {
        title: "Малко сън — каскадата започва",
        steps: [
          { emoji: "😴", label: `Сън ${hours.toFixed(1)}ч`, delta: "недостиг" },
          { emoji: "🧠", label: "Когнитивен утре", delta: "−12%" },
          { emoji: "⚡", label: "Стрес/кортизол", delta: "+15%" },
          { emoji: "💉", label: "Инсулинова чувств.", delta: "−18%" },
        ],
      };
    }
    return {
      title: "Възстановителен сън",
      steps: [
        { emoji: "😴", label: `Сън ${hours.toFixed(1)}ч`, delta: "добър" },
        { emoji: "🧠", label: "Глимфатно почистване", delta: "активно" },
        { emoji: "🦋", label: "Щитовидна", delta: "T3 ↑" },
        { emoji: "🌸", label: "Хормонален", delta: "възстановяване" },
      ],
    };
  },
  "lab-uploaded": () => ({
    title: "Нова лаборатория интегрирана",
    steps: [
      { emoji: "🧪", label: "Лабораторен", delta: "обновен" },
      { emoji: "🩸", label: "Маркери", delta: "анализирани" },
      { emoji: "🎗️", label: "BHI преразглежда", delta: "estrogen ос" },
      { emoji: "📊", label: "Twin score", delta: "преизчислен" },
    ],
  }),
  "stress-rated": (payload = {}) => {
    const level = Number(payload.level ?? 5);
    if (level >= 7) {
      return {
        title: "Висок стрес — пълна верига",
        steps: [
          { emoji: "⚡", label: `Стрес ${level}/10`, delta: "висок" },
          { emoji: "🦋", label: "Щитовидна", delta: "T4→T3 −8%" },
          { emoji: "💉", label: "Кортизол → глюкоза", delta: "+12%" },
          { emoji: "🌸", label: "Овулация", delta: "възможно забавяне" },
        ],
      };
    }
    return null;
  },
};

// Backwards-compatible aliases — older code dispatches "meal-added" /
// "meal-log-changed" without payload; reuse the same cascade builder.
CASCADES["meal-added"] = CASCADES["meal-logged"];
CASCADES["meal-log-changed"] = CASCADES["meal-logged"];
CASCADES["nutramilo:meals:updated"] = CASCADES["meal-logged"];

export default function TwinCascadeToast() {
  const [cascade, setCascade] = useState(null);
  const [phase, setPhase] = useState(0); // 0=entering, 1-4=step revealed, 5=exiting

  useEffect(() => {
    function handler(e) {
      const builder = CASCADES[e.type];
      if (!builder) return;
      const result = builder(e.detail || {});
      if (!result) return;
      setCascade(result);
      setPhase(0);
    }
    const events = Object.keys(CASCADES);
    events.forEach((ev) => window.addEventListener(ev, handler));
    return () => events.forEach((ev) => window.removeEventListener(ev, handler));
  }, []);

  useEffect(() => {
    if (!cascade) return;
    // Stagger the reveal: each step appears every 600ms; full cascade lives ~5s.
    const timers = [];
    [1, 2, 3, 4].forEach((p, idx) => {
      timers.push(setTimeout(() => setPhase(p), 350 + idx * 600));
    });
    timers.push(setTimeout(() => setPhase(5), 5500)); // start exit
    timers.push(setTimeout(() => setCascade(null), 6000)); // remove
    return () => timers.forEach(clearTimeout);
  }, [cascade]);

  if (!cascade) return null;

  return (
    <div
      data-testid="twin-cascade-toast"
      className={`mb-3 p-3 rounded-xl border bg-gradient-to-r from-pink-50 via-amber-50 to-emerald-50 dark:from-pink-500/10 dark:via-amber-500/10 dark:to-emerald-500/10 border-amber-200/60 dark:border-amber-500/20 transition-all duration-500 ${
        phase === 5 ? "opacity-0 translate-y-[-6px]" : "opacity-100 translate-y-0"
      }`}
    >
      <div className="text-[11px] uppercase font-bold opacity-70 mb-2 flex items-center gap-2">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
        {cascade.title} · каскада в реално време
      </div>
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
        {cascade.steps.map((s, i) => {
          const visible = phase >= i + 1;
          return (
            <React.Fragment key={i}>
              <div
                className={`flex-shrink-0 px-2 py-1.5 rounded-lg bg-white dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-700/40 transition-all duration-500 ${
                  visible ? "opacity-100 translate-x-0 scale-100" : "opacity-0 -translate-x-2 scale-95"
                }`}
                data-testid={`twin-cascade-step-${i}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-base">{s.emoji}</span>
                  <div className="text-left">
                    <div className="text-[11px] font-semibold leading-tight">{s.label}</div>
                    <div className="text-[10px] opacity-60 leading-tight">{s.delta}</div>
                  </div>
                </div>
              </div>
              {i < cascade.steps.length - 1 && (
                <span
                  className={`text-amber-500 transition-all duration-500 ${
                    phase >= i + 1 ? "opacity-100" : "opacity-0"
                  }`}
                  aria-hidden
                >
                  →
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
